import { useMemo } from "react";
import moment from "moment";
import useSWR, { unstable_serialize, useSWRConfig } from "swr";

import { botConfigsEndpoints, type BotConfig } from "../api";
import useHomeHistoryCache from "./homeHistoryCache";

// Charts are independent of current balances and bot states. Keep their requests
// out of the initial card fetch, and scope every cache entry to its user and IDs.
export default function useHomeHistory(userId: number | string | undefined, bots: BotConfig[], ready: boolean) {
  // Share the exact window with the server and card. Five-minute clock buckets
  // avoid making a new cache key on every render or quick return to Home.
  const windowAnchor = Math.floor(Date.now() / (5 * 60 * 1000)) * (5 * 60 * 1000);
  const historySince = useMemo(() => moment(windowAnchor).subtract(1, "month").valueOf(), [windowAnchor]);
  const ids = useMemo(() => bots.map(bot => bot.id).sort((a, b) => Number(a) - Number(b)), [bots]);
  const { cache } = useSWRConfig();
  const key = userId && ready && ids.length ? ["home-bot-history-window", userId, ids, historySince] : null;
  const afterRequest = useHomeHistoryCache(cache, unstable_serialize(key), userId);
  const history = useSWR(
    key,
    async () => {
      try {
        return (await botConfigsEndpoints.getMyBotConfigs({
          filters: { id: { $in: ids } },
          populate: ["statistics.history"],
          historyMaxPoints: 512,
          historySince,
          pagination: { pageSize: 100 },
        })).data;
      } finally { afterRequest(); }
    },
    // The owned LRU keeps warm results; avoid retaining obsolete fetch promises
    // for a full five-minute window in SWR's separate deduplication registry.
    { revalidateOnFocus: false, keepPreviousData: false, dedupingInterval: 2000, errorRetryCount: 2 },
  );

  const botsWithHistory = useMemo(() => {
    const byId = new Map(history.data?.map(bot => [bot.id, bot]));
    return bots.map(bot => {
      const historical = byId.get(bot.id);
      // Never overwrite a live pause, balance or configuration with the older
      // snapshot returned by a potentially slow historical request.
      return historical ? { ...bot, statistics: { ...bot.statistics, history: historical.statistics?.history } } : bot;
    });
  }, [bots, history.data]);

  return { ...history, bots: botsWithHistory, historySince };
}
